import React, { createContext, useCallback, useContext, useState } from 'react';
import ConfirmDialog from '../components/common/ConfirmDialog';

const ConfirmContext = createContext(null);

/**
 * ConfirmProvider — renders a single themed confirmation dialog on demand.
 * Wrap the app once; call useConfirm() anywhere to get an async confirm().
 *
 *   const confirm = useConfirm();
 *   if (await confirm({ title: 'Delete file?', danger: true })) { ... }
 */
export function ConfirmProvider({ children }) {
    const [dialog, setDialog] = useState(null); // { options, resolve }

    const confirm = useCallback((options = {}) => {
        return new Promise((resolve) => {
            setDialog({ options, resolve });
        });
    }, []);

    const settle = useCallback((result) => {
        setDialog((current) => {
            current?.resolve(result);
            return null;
        });
    }, []);

    return (
        <ConfirmContext.Provider value={confirm}>
            {children}
            {dialog && (
                <ConfirmDialog
                    {...dialog.options}
                    onConfirm={() => settle(true)}
                    onCancel={() => settle(false)}
                />
            )}
        </ConfirmContext.Provider>
    );
}

/**
 * useConfirm — returns confirm(options) => Promise<boolean>. Falls back to the
 * native window.confirm if used outside a provider (defensive; the provider is
 * mounted at the app root).
 */
export function useConfirm() {
    const ctx = useContext(ConfirmContext);
    if (!ctx) {
        return ({ message, title } = {}) =>
            Promise.resolve(window.confirm(message || title || 'Are you sure?'));
    }
    return ctx;
}

export default ConfirmContext;
